import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Subject } from 'rxjs';

import { Broadcaster } from './events-v7';

declare var global: any;

@Injectable()
export class TranslationService {
  public static translation: Map<string, string> = new Map<string, string>();
  public static language = '';
  // Must be set on path constants

  public static PRODUCT_PATH = 'PRODUCT_PATH';
  public static USE_TRANSLATION_SERVER = 'USE_TRANSLATION_SERVER';
  public static TRANSLATION_BASE_URL = 'TRANSLATION_BASE_URL';

  public labels: any;
  public _useTranslationServer = false;

  public productPath = '';
  public _localeData: Subject<any> = new Subject<any>();

  /**
   * st comes from static-translation
   */
  public static st(textID: string): string {
    const textValue = this.translation.get(textID);
    if (!textValue || textValue === undefined) {
      return 'NOT:' + textID;
    }
    return textValue;
  }

  /**
   * gets all translations
   */
  public static all(): Map<string, string> {
    const result = this.translation;
    return result;
  }

  constructor(
    public eventBroadcaster: Broadcaster,
    public http: HttpClient,
    @Inject(TranslationService.PRODUCT_PATH)
    @Optional()
    public _productPath: string,
    @Inject(TranslationService.USE_TRANSLATION_SERVER)
    @Optional()
    public useTranslationServer: boolean,
    @Inject(TranslationService.TRANSLATION_BASE_URL)
    @Optional()
    public _translationUrl: string
  ) {
    this.productPath = _productPath || '/';
    this._useTranslationServer = useTranslationServer || false;

    if (this._translationUrl === null) {
      this._translationUrl = '/translate/translate';
    }

    if (this.isRunningOnBrowser()) {
      const comingFromReact =
        (global as any).sessionStorage.getItem('language') || 'en_US';
      let langLocalStorage = (global as any).localStorage.getItem('language');
      if (comingFromReact.indexOf('es') !== -1 && !this._useTranslationServer) {
        langLocalStorage = 'es';
        (global as any).localStorage.setItem('language', 'es');
      } else if (
        comingFromReact.indexOf('en') !== -1 &&
        !this._useTranslationServer
      ) {
        langLocalStorage = 'en';
        (global as any).localStorage.setItem('language', 'en');
      } else {
        langLocalStorage = comingFromReact;
        (global as any).localStorage.setItem('language', langLocalStorage);
      }
      let localLang: string;
      if (this._useTranslationServer) {
        this.lang(langLocalStorage ? langLocalStorage : 'en_US');
        localLang = langLocalStorage ? langLocalStorage : 'en_US';
      } else {
        this.lang(langLocalStorage ? langLocalStorage : 'en');
        localLang = langLocalStorage ? langLocalStorage : 'en';
      }

      // add this next line to correct react projects

      if (localLang === 'en') {
        localLang = localLang + '_US';
      } else if (localLang === 'es') {
        localLang = localLang + '_MX';
      }
      (global as any).sessionStorage.setItem('language', localLang);
    } else {
      if (this._useTranslationServer) {
        this.lang('en_US');
      } else {
        this.lang('en');
      }
    }
  }

  public get localeData() {
    return this._localeData.asObservable();
  }

  public isRunningOnBrowser(): boolean {
    return (global as any) !== null && (global as any) !== undefined;
  }

  public onChange(value: any): void {
    this.labels = value;
    this._localeData.next(value);
  }

  /**
   * this method is left here for backwards compatibility
   */
  public lang(lang: string): void {
    if (lang === TranslationService.language) {
      return;
    }
    TranslationService.language = lang;
    // add this next line to correct react projects, need to correct this
    let localLang: string = TranslationService.language;
    if (localLang === 'en') {
      localLang = localLang + '_US';
    } else if (localLang === 'es') {
      localLang = localLang + '_MX';
    }
    if (this.isRunningOnBrowser()) {
      sessionStorage.setItem('language', localLang);
    }
    // if the language is only 2 letters it might be a legacy application
    if (!this._useTranslationServer) {
      this.http
        .get(this.productPath + 'vendor/locale-' + lang + '.json')
        .subscribe({
          next: response => this.populateTranslation(response),
          error: error => this.handleError(error)
        });
    } else {
      // if the translation name is bigger than 2 letters, request to external server
      this.http
        .get(this._translationUrl + this.productPath + localLang)
        .subscribe({
          next: response => this.populateTranslation(response),
          error: error => this.handleError(error)
        });
    }
  }

  public isoLang(lang: string): void {
    // must contain either one of these symbols
    if (lang.indexOf('-') !== -1 || lang.indexOf('_') !== -1) {
      TranslationService.language = lang;
      (global as any).sessionStorage.setItem('language', lang);
    }
  }

  public file(url: string): void {
    this.http.get(url).subscribe({
      next: response => this.populateTranslation(response),
      error: error => this.handleError(error)
    });
  }

  /**
   * pt comes from public-translation
   */
  public pt(textID: string): string {
    const textValue = TranslationService.translation.get(textID);
    if (!textValue || textValue === undefined) {
      return 'NOT:' + textID;
    }

    return textValue;
  }

  public getLabel(textId: string): string {
    const textValue = TranslationService.translation.get(textId);
    if (!textValue) {
      return "";
    }
    return textValue;
  }

  public getlang(): string {
    return TranslationService.language;
  }

  public getCountryCode(): string {
    return TranslationService.language.split('_')[1];
  }

  public populateTranslation(result: any) {
    for (const item of Object.keys(result)) {
      TranslationService.translation.set(item, result[item]);
    }
    this.eventBroadcaster.broadcast(
      Broadcaster.DCM_LANGUAGE_FETCHED,
      TranslationService.language
    );
    this.onChange(result);
  }

  public handleError(error: any): Promise<any> {
    return Promise.reject(error.message || error);
  }
}
